/**
 * VisitorShareDialog — lightweight share flow for visitors after booking.
 * Uses native share when available, falls back to copy actions.
 * Reuses existing share infrastructure (UTM, OG proxy, captions).
 */

import { useState, useCallback } from 'react';
import { Share2, Copy, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { track } from '@/lib/analytics';
import { generateVisitorCaption } from './visitorCaption';
import { buildVisitorShareUrl } from './visitorShareUrl';
import type { ActivityShareTarget } from '../ShareTarget';

interface VisitorShareDialogProps {
  target: ActivityShareTarget;
  onClose: () => void;
}

async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    return true;
  } catch {
    return false;
  }
}

export function VisitorShareDialog({ target, onClose }: VisitorShareDialogProps) {
  const [copied, setCopied] = useState(false);

  const shareUrl = buildVisitorShareUrl(target.id);
  const caption = generateVisitorCaption(target);
  const fullShareText = `${caption}\n\n${shareUrl}`;

  const handleNativeShare = useCallback(async () => {
    track('visitor_share_clicked', {
      activity_id: target.id,
      method: 'native',
    });

    const shareData: ShareData = {
      text: caption,
      url: shareUrl,
    };

    try {
      if (typeof navigator.share === 'function' && (!navigator.canShare || navigator.canShare(shareData))) {
        await navigator.share(shareData);
        track('visitor_share_completed', { activity_id: target.id, method: 'native' });
        return;
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
    }

    // Fallback: copy everything
    const ok = await copyText(fullShareText);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
      toast.success('Text och länk kopierad!');
      track('visitor_share_completed', { activity_id: target.id, method: 'clipboard' });
    } else {
      toast.error('Kunde inte kopiera. Prova manuellt.');
    }
  }, [target.id, caption, shareUrl, fullShareText]);

  const handleCopyLink = useCallback(async () => {
    track('visitor_share_clicked', { activity_id: target.id, method: 'copy_link' });
    const ok = await copyText(shareUrl);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
      toast.success('Länk kopierad!');
    }
  }, [target.id, shareUrl]);

  return (
    <div className="rounded-xl border border-border bg-muted/10 p-4 space-y-3 animate-in fade-in slide-in-from-bottom-2 duration-200">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
            <Share2 className="h-4 w-4 text-primary" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">Tipsa en vän</p>
            <p className="text-xs text-muted-foreground">Dela aktiviteten med någon du vill ta med</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded-md text-muted-foreground/50 hover:text-muted-foreground hover:bg-muted/40 transition-colors"
          aria-label="Stäng"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Caption preview */}
      <div className="rounded-lg bg-muted/20 border border-border/50 p-3">
        <p className="text-xs text-muted-foreground whitespace-pre-line leading-relaxed">{caption}</p>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <Button
          className="flex-1 gap-2"
          onClick={handleNativeShare}
        >
          <Share2 className="h-4 w-4" />
          Dela aktiviteten
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={handleCopyLink}
          className="shrink-0"
          title="Kopiera länk"
        >
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        </Button>
      </div>

      <p className="text-[10px] text-muted-foreground text-center">
        Vi delar aldrig något utan ditt godkännande.
      </p>
    </div>
  );
}
